import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import CircularProgress from 'components/shared/CircularProgress';
import { simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import {
  BenefitAttachment,
  getBenefitAttachmentUrl,
  getEligibilityPdfUrl,
  indexVerificationHistory,
  VerificationRun,
} from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

import { coveragesOf, coverageTabLabel } from './coverageTabs';
import { useOpenSignedUrl } from './useOpenSignedUrl';

// Every verification this referral has run, across coverages (OLIO-2794).
//
// Fetched on its own rather than read from the referral: a re-run retires the previous result, so the
// referral payload — which carries only current values — holds just the latest check per coverage. Past
// runs come from GET /workflows/:id/verification_history, and each row downloads its OWN document by
// naming its run, not the coverage's most recent one.
//
// Only mounted while this tab is open, so a referral nobody opens History on never fetches it.
export default function CoverageHistory({ workflow }: { workflow: Workflow }) {
  const [newestFirst, setNewestFirst] = useState(true);
  const { addToast } = useToastActions();
  const openSignedUrl = useOpenSignedUrl();

  const { data: runs, isPending } = useQuery({
    queryKey: ['workflows', 'verificationHistory', workflow.id],
    queryFn: ({ signal }) => indexVerificationHistory(workflow.id, signal),
  });

  // Which coverage a run belongs to, by the same words the tabs use — so a row reads "Secondary" and
  // the Secondary tab shows that same coverage. Positional, because a coverage's own label is a payer.
  const labelForFacet = useMemo(() => {
    const order = coveragesOf(workflow);
    return (facetId: string | null) => {
      // The coverage's own rank, not its place in this list (OLIO-3141) — the same thing the tab
      // strip reads, which is what keeps the promise above true when a rank is skipped.
      const facet = order.find((coverage) => coverage.id === facetId);
      // A run from before this referral had coverages, or one whose coverage has since been archived —
      // its history stays, so it needs a word rather than a blank cell.
      return facet ? coverageTabLabel(facet.position) : 'Referral';
    };
  }, [workflow]);

  const sorted = useMemo(() => {
    const rows = [...(runs ?? [])];
    rows.sort((a, b) => (newestFirst ? 1 : -1) * (Date.parse(b.completedAt) - Date.parse(a.completedAt)));
    return rows;
  }, [runs, newestFirst]);

  // A person's name when a person asked for the run; "Olio" when the engine started it itself because
  // the inputs had arrived. Never the engine's user record — "AI Bot User" is an implementation detail,
  // and every other surface says Olio.
  const ranBy = (run: VerificationRun) => {
    if (run.ranBy.kind === 'user') return run.ranBy.name || '—';

    return run.ranBy.kind ? 'Olio' : '—';
  };

  const download = (run: VerificationRun) => {
    getEligibilityPdfUrl(workflow.id, run.facetId, run.eligibilityCheckId)
      .then(({ url }) => window.open(url, '_blank'))
      .catch(() => addToast({ text: 'Could not download the PDF. Please try again.' }));
  };

  const openFile = (file: BenefitAttachment) =>
    openSignedUrl(() => getBenefitAttachmentUrl(workflow.id, file.id), file.contentType);

  if (isPending)
    return (
      <Centered data-testid='history-loading'>
        <CircularProgress color={colors.blue100} />
      </Centered>
    );

  if (sorted.length === 0) {
    return (
      <Centered data-testid='history-empty'>
        <BodySmall>No benefit verifications have run yet.</BodySmall>
      </Centered>
    );
  }

  return (
    <Table data-testid='coverage-history'>
      <HeaderRow>
        <HeaderCell>Type</HeaderCell>
        <HeaderCell>Ran By</HeaderCell>
        <SortableHeader type='button' onClick={() => setNewestFirst((current) => !current)} data-testid='sort-ran-on'>
          Ran On
          {/* The icon carries the current direction, so the header shows which way it's sorted rather
              than just that it can be. */}
          <ArrowUpDownIcon width={10} height={10} direction={newestFirst ? 'desc' : 'asc'} />
        </SortableHeader>
        <HeaderCell />
      </HeaderRow>
      {sorted.map((run) => (
        <Row key={run.id} data-testid='history-row'>
          <Cell>
            {labelForFacet(run.facetId)}
            {run.source === 'manual' ? ' · Manual' : ''}
          </Cell>
          <Cell>{ranBy(run)}</Cell>
          <Cell>{simpleDateAtTime(run.completedAt)}</Cell>
          <ActionCell>
            {/* No link when that run produced no document — an errored check or a payer that isn't
                enrolled. A link that 404s reads as a broken page rather than as "there isn't one". */}
            {run.eligibilityCheckId && (
              <PdfLink type='button' onClick={() => download(run)} data-testid='history-download-pdf'>
                Download PDF
              </PdfLink>
            )}
            {run.source === 'manual' &&
              (run.attachments ?? []).map((file) => (
                <PdfLink key={file.id} type='button' onClick={() => openFile(file)} data-testid='history-open-file'>
                  {file.filename}
                </PdfLink>
              ))}
          </ActionCell>
        </Row>
      ))}
    </Table>
  );
}

// ONE grid for the whole table, with the rows as `display: contents` so their cells become items of
// it. Every row used to be its own grid, which meant the `auto` action column was measured against
// that row's own content — a run with no PDF left it zero-width, handed the slack to the other three
// columns, and its date no longer lined up with the row above it. Measured once, the columns agree.
const Table = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1.6fr auto;
  /* stretch, not center: each cell paints its own background and rule now, so a short cell next to a
     taller one would otherwise leave a gap in the header band and a break in the row divider. */
  align-items: stretch;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: contents;
`;

const Row = styled.div`
  display: contents;
`;

// Spacing and rules live on the cells now, since the rows no longer generate a box of their own.
// `:first-child` / `:last-child` still resolve per row — `display: contents` removes the box, not
// the parent-child relationship.
const cell = css`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 14px 8px;

  &:first-child {
    padding-left: 20px;
  }

  &:last-child {
    padding-right: 20px;
  }
`;

const headerCell = css`
  ${cell}
  background: ${colors.black3};
`;

const bodyCell = css`
  ${cell}
  border-top: 1px solid ${colors.black10};
`;

const HeaderCell = styled(BodySmall)`
  ${headerCell}
  color: ${colors.black50};
`;

const SortableHeader = styled.button`
  ${headerCell}
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${colors.black3};
  border: none;
  font-family: inherit;
  font-size: ${({ theme }) => theme.textStyles.bodySmall.fontSize};
  font-weight: 500;
  color: ${colors.black100};
  cursor: pointer;
`;

const Cell = styled(BodySmall)`
  ${bodyCell}
  color: ${colors.black100};
`;

const ActionCell = styled.div`
  ${bodyCell}
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
`;

const PdfLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.textStyles.bodySmall.fontSize};
  font-weight: 500;
  color: ${colors.blue100};
  cursor: pointer;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;
